import {
  ArrowPathIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxDescription,
  ComboboxLabel,
  ComboboxOption,
} from '@/components/ui/catalyst/combobox';
import { DropdownItem } from '@/components/ui/catalyst/dropdown';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Blob as BlobType } from '@/services/types/blob-types';
import { CollectionNode } from '@/services/types/collection-types';

import {
  useDestroyBlobMutation,
  useMoveBlobToMatterMutation,
  useUpdateBlobMutation,
} from '../../../services/api/blobService';
import { useGetCollectionTreeQuery } from '../../../services/api/collectionService';
import {
  useGetMatterQuery,
  useGetMattersQuery,
} from '../../../services/api/mattersService';
import { TreeViewSize } from '../constants/treeStyles';
import { ActionDialog } from './ActionDialog';
import { ActionsMenu } from './ActionsMenu';

interface FileActionsProps {
  file: BlobType;
  collectionTree: CollectionNode;
  onSuccess: () => void;
  size?: TreeViewSize;
}

export const FileActions = ({
  file,
  collectionTree,
  onSuccess,
  size = 'sm',
}: FileActionsProps) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [newName, setNewName] = useState(file.name);
  const [moveToCollectionId, setMoveToCollectionId] = useState(
    file.collection_details.id,
  );
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);
  const [matterSearchInput, setMatterSearchInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [updateBlob] = useUpdateBlobMutation();
  const [destroyBlob] = useDestroyBlobMutation();
  const [moveBlobToMatter] = useMoveBlobToMatterMutation();

  // Debounce search input for server-side filtering
  const debouncedMatterSearch = useDebouncedValue(matterSearchInput, 300);

  // Fetch current matter details to get its name
  const { data: currentMatter } = useGetMatterQuery(collectionTree.matter_id, {
    skip: !isMoveDialogOpen,
  });

  // Fetch matters for cross-matter move
  const { data: mattersData } = useGetMattersQuery(
    { search: debouncedMatterSearch || undefined, limit: 20 },
    { skip: !isMoveDialogOpen },
  );

  // Fetch collection tree for selected matter (if different from current)
  const { data: targetCollectionTree } = useGetCollectionTreeQuery(
    selectedMatterId || '',
    {
      skip: !selectedMatterId || selectedMatterId === collectionTree.matter_id,
    },
  );

  // Use target collection tree if a different matter is selected, otherwise use current
  const activeCollectionTree = useMemo(() => {
    if (
      selectedMatterId &&
      selectedMatterId !== collectionTree.matter_id &&
      targetCollectionTree
    ) {
      return targetCollectionTree;
    }
    return collectionTree;
  }, [selectedMatterId, collectionTree, targetCollectionTree]);

  // Build combobox options with current matter as first option
  type MatterOption = { id: string; name: string; firmName?: string };
  const matterOptions: MatterOption[] = useMemo(() => {
    const currentMatterName = currentMatter?.name || 'Current matter';
    const currentFirmName = currentMatter?.firm?.name;
    const otherMatters =
      mattersData?.results?.filter((m) => m.id !== collectionTree.matter_id) ||
      [];
    return [
      { id: '', name: currentMatterName, firmName: currentFirmName },
      ...otherMatters.map((m) => ({
        id: m.id,
        name: m.name,
        firmName: m.firm?.name,
      })),
    ];
  }, [
    mattersData,
    collectionTree.matter_id,
    currentMatter?.name,
    currentMatter?.firm?.name,
  ]);

  // Get the selected matter option for the combobox
  const selectedMatterOption = useMemo(() => {
    return (
      matterOptions.find((opt) => opt.id === (selectedMatterId || '')) ||
      matterOptions[0]
    );
  }, [matterOptions, selectedMatterId]);

  const handleRename = async () => {
    try {
      await updateBlob({
        matterId: collectionTree.matter_id,
        blob: {
          id: file.id,
          name: newName,
        },
        blobId: file.id,
      }).unwrap();
      setIsRenameDialogOpen(false);
      onSuccess();
    } catch (e) {
      setError('Failed to rename file');
    }
  };

  const handleMove = async () => {
    try {
      // Check if moving to a different matter
      if (selectedMatterId && selectedMatterId !== collectionTree.matter_id) {
        await moveBlobToMatter({
          matterId: collectionTree.matter_id,
          blobId: file.id,
          body: {
            target_matter_id: selectedMatterId,
            target_collection_id: moveToCollectionId,
            delete_source: true,
          },
        }).unwrap();
      } else {
        // Same matter move
        await updateBlob({
          matterId: collectionTree.matter_id,
          blob: {
            id: file.id,
            collection: moveToCollectionId,
          },
          blobId: file.id,
        }).unwrap();
      }
      setIsMoveDialogOpen(false);
      setSelectedMatterId(null);
      setMatterSearchInput('');
      onSuccess();
    } catch (e) {
      setError('Failed to move file');
    }
  };

  const handleDelete = async () => {
    try {
      await destroyBlob({
        matterId: collectionTree.matter_id,
        blobId: file.id,
      }).unwrap();
      setIsDeleteDialogOpen(false);
      onSuccess();
    } catch (e) {
      setError('Failed to delete file');
    }
  };

  const renderCollectionOptions = (node: CollectionNode, level = 0) => {
    const options = [];
    const prefix = '\u00A0'.repeat(level * 2);

    options.push(
      <option key={node.id} value={node.id}>
        {prefix + (node.name === 'root' ? 'All Files' : node.name)}
      </option>,
    );

    if (node.children) {
      node.children.forEach((child) => {
        options.push(...renderCollectionOptions(child, level + 1));
      });
    }

    return options;
  };

  const handleActionClick = (
    e: React.MouseEvent<HTMLElement>,
    action: () => void,
  ) => {
    e.stopPropagation();
    e.preventDefault();
    action();
  };

  const handleKeyDown = (e: React.KeyboardEvent<Element>) => {
    e.stopPropagation();
  };

  return (
    <>
      <ActionsMenu onKeyDown={handleKeyDown}>
        <DropdownItem
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleActionClick(e, () => setIsRenameDialogOpen(true))
          }
        >
          <PencilIcon data-slot="icon" />
          Rename
        </DropdownItem>
        <DropdownItem
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleActionClick(e, () => setIsMoveDialogOpen(true))
          }
        >
          <ArrowPathIcon data-slot="icon" />
          Move
        </DropdownItem>
        <DropdownItem
          onClick={(e: React.MouseEvent<HTMLElement>) =>
            handleActionClick(e, () => setIsDeleteDialogOpen(true))
          }
        >
          <TrashIcon data-slot="icon" className="text-red-500" />
          <span className="text-red-600">Delete</span>
        </DropdownItem>
      </ActionsMenu>

      {/* Rename Dialog */}
      <ActionDialog
        isOpen={isRenameDialogOpen}
        onClose={() => setIsRenameDialogOpen(false)}
        title="Rename File"
        description="Enter a new name for the file."
        confirmButtonText="Rename"
        onConfirm={handleRename}
        errorMessage={error}
        inputValue={newName}
        onInputChange={setNewName}
      />

      {/* Move Dialog */}
      {isMoveDialogOpen && (
        <ActionDialog
          isOpen={isMoveDialogOpen}
          onClose={() => {
            setIsMoveDialogOpen(false);
            setSelectedMatterId(null);
            setMatterSearchInput('');
          }}
          title="Move File"
          description="Select a destination folder, or move to a different matter."
          confirmButtonText="Move"
          onConfirm={handleMove}
          errorMessage={error}
        >
          <div className="space-y-4">
            <div>
              <span className="block text-sm font-medium text-zinc-700 mb-1">
                Matter
              </span>
              <Combobox
                value={selectedMatterOption}
                onChange={(opt) => {
                  const newMatterId = opt?.id || null;
                  setSelectedMatterId(newMatterId);
                  // Reset collection selection when matter changes
                  if (newMatterId && newMatterId !== collectionTree.matter_id) {
                    // Will be set to root when targetCollectionTree loads
                    setMoveToCollectionId('');
                  } else {
                    setMoveToCollectionId(file.collection_details.id);
                  }
                }}
                options={matterOptions}
                by="id"
                displayValue={(opt) => opt?.name ?? ''}
                filter={(opt, query) =>
                  opt.name.toLowerCase().includes(query.toLowerCase()) ||
                  (opt.firmName?.toLowerCase().includes(query.toLowerCase()) ??
                    false)
                }
                onInputChange={setMatterSearchInput}
                aria-label="Select matter"
                placeholder="Search matters..."
                panelClassName="z-[60]"
              >
                {(option) => (
                  <ComboboxOption key={option.id} value={option}>
                    <ComboboxLabel>{option.name}</ComboboxLabel>
                    {option.firmName && (
                      <ComboboxDescription>
                        {option.firmName}
                      </ComboboxDescription>
                    )}
                  </ComboboxOption>
                )}
              </Combobox>
            </div>
            <div>
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor="destination-folder"
                className="block text-sm font-medium text-zinc-700 mb-1"
              >
                Destination folder
              </label>
              <select
                id="destination-folder"
                value={moveToCollectionId}
                onChange={(e) => setMoveToCollectionId(e.target.value)}
                className="w-full px-3 py-2 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {renderCollectionOptions(activeCollectionTree)}
              </select>
            </div>
          </div>
        </ActionDialog>
      )}

      {/* Delete Dialog */}
      <ActionDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete File"
        description={`Are you sure you want to delete "${file.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        onConfirm={handleDelete}
        isDangerousAction
      />
    </>
  );
};
